(() => {
  const REVISION = "2024-06-15";
  const WRAPPER_SELECTOR = "[data-klaviyo-bis]";
  const FORM_SELECTOR = ".klaviyo-bis__form";
  const VARIANTS_SCRIPT_SELECTOR = "script[data-klaviyo-bis-variants]";
  const FIELDS_SELECTOR = ".klaviyo-bis__fields";
  const SUCCESS_SELECTOR = ".klaviyo-bis__success";
  const ERROR_SELECTOR = ".klaviyo-bis__error";
  const SUBMIT_SELECTOR = ".klaviyo-bis__submit";
  const EMAIL_SELECTOR = 'input[name="email"]';
  const MODAL_SELECTOR = "dialog-element.klaviyo-bis__modal";

  const variantCache = new WeakMap();

  const buildVariantMap = (variants) => {
    const map = new Map();
    variants.forEach((variant) => {
      if (variant && variant.id != null) {
        map.set(String(variant.id), variant);
      }
    });
    return map;
  };

  const getVariantMap = (wrapper) => {
    if (variantCache.has(wrapper)) {
      return variantCache.get(wrapper);
    }

    const script = wrapper.querySelector(VARIANTS_SCRIPT_SELECTOR);
    if (!script) {
      variantCache.set(wrapper, null);
      return null;
    }

    try {
      const variants = JSON.parse(script.textContent || "[]");
      const map = buildVariantMap(Array.isArray(variants) ? variants : []);
      variantCache.set(wrapper, map);
      return map;
    } catch (error) {
      variantCache.set(wrapper, null);
      return null;
    }
  };

  const getVariantById = (wrapper, variantId) => {
    if (!variantId) {
      return null;
    }

    const map = getVariantMap(wrapper);
    if (!map) {
      return null;
    }

    return map.get(String(variantId)) || null;
  };

  const findWrapperForForm = (form) => {
    if (!form) {
      return null;
    }

    const formId = form.getAttribute("id");
    if (!formId) {
      return null;
    }

    return document.querySelector(
      `${WRAPPER_SELECTOR}[data-product-form-id="${CSS.escape(formId)}"]`
    );
  };

  const getVariantIdFromForm = (form) => {
    return form?.querySelector('input[name="id"]')?.value || null;
  };

  const updateWrapperState = (wrapper, variant) => {
    if (!wrapper) {
      return;
    }

    const companyId = (wrapper.dataset.klaviyoCompanyId || "").trim();
    if (!companyId) {
      wrapper.hidden = true;
      return;
    }

    if (!variant) {
      wrapper.hidden = true;
      return;
    }

    wrapper.hidden = !!variant.available;
    if (variant.id != null) {
      wrapper.dataset.variantId = String(variant.id);
    }
  };

  const updateSubmitState = (form) => {
    const emailInput = form.querySelector(EMAIL_SELECTOR);
    const submitButton = form.querySelector(SUBMIT_SELECTOR);
    if (!emailInput || !submitButton) {
      return;
    }

    if (emailInput.value && emailInput.checkValidity()) {
      submitButton.removeAttribute("disabled");
    } else {
      submitButton.setAttribute("disabled", "");
    }
  };

  const resetFormState = (form) => {
    const emailInput = form.querySelector(EMAIL_SELECTOR);
    const successBanner = form.querySelector(SUCCESS_SELECTOR);
    const errorBanner = form.querySelector(ERROR_SELECTOR);
    const fields = form.querySelector(FIELDS_SELECTOR);
    const submitButton = form.querySelector(SUBMIT_SELECTOR);

    successBanner?.setAttribute("hidden", "");
    errorBanner?.setAttribute("hidden", "");
    fields?.removeAttribute("hidden");

    if (emailInput) {
      emailInput.value = "";
    }

    submitButton?.removeAttribute("aria-busy");
    updateSubmitState(form);
  };

  const resetWrapperState = (wrapper) => {
    if (!wrapper) {
      return;
    }

    wrapper.querySelectorAll(FORM_SELECTOR).forEach((form) => {
      resetFormState(form);
    });
  };

  const handleVariantUpdate = (form, variantFromEvent) => {
    const wrapper = findWrapperForForm(form);
    if (!wrapper) {
      return;
    }

    if (variantFromEvent) {
      updateWrapperState(wrapper, variantFromEvent);
      return;
    }

    const variantId =
      getVariantIdFromForm(form) || wrapper.dataset.initialVariantId;
    const variant = getVariantById(wrapper, variantId);
    updateWrapperState(wrapper, variant);
  };

  const initWrappers = () => {
    document.querySelectorAll(WRAPPER_SELECTOR).forEach((wrapper) => {
      const formId = wrapper.dataset.productFormId;
      const form = formId ? document.getElementById(formId) : null;
      const variantId =
        getVariantIdFromForm(form) || wrapper.dataset.initialVariantId;
      const variant = getVariantById(wrapper, variantId);
      updateWrapperState(wrapper, variant);

      wrapper.querySelectorAll(FORM_SELECTOR).forEach((bisForm) => {
        updateSubmitState(bisForm);
      });
    });
  };

  const resolveWrapperFromForm = (form) => {
    const directWrapper = form.closest(WRAPPER_SELECTOR);
    if (directWrapper) {
      return directWrapper;
    }

    const modal = form.closest("dialog-element");
    const bisId = modal?.dataset?.bisId;
    if (bisId) {
      return document.querySelector(
        `${WRAPPER_SELECTOR}[data-bis-id="${CSS.escape(bisId)}"]`
      );
    }

    return null;
  };

  const submitForm = async (form) => {
    const wrapper = resolveWrapperFromForm(form);
    if (!wrapper) {
      return;
    }

    const emailInput = form.querySelector(EMAIL_SELECTOR);
    if (!emailInput) {
      return;
    }

    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    const companyId = (wrapper.dataset.klaviyoCompanyId || "").trim();
    const variantId =
      wrapper.dataset.variantId || wrapper.dataset.initialVariantId;

    const successBanner = form.querySelector(SUCCESS_SELECTOR);
    const errorBanner = form.querySelector(ERROR_SELECTOR);
    const fields = form.querySelector(FIELDS_SELECTOR);
    const submitButton = form.querySelector(SUBMIT_SELECTOR);

    successBanner?.setAttribute("hidden", "");
    errorBanner?.setAttribute("hidden", "");

    if (!companyId || !variantId) {
      errorBanner?.removeAttribute("hidden");
      return;
    }

    submitButton?.setAttribute("disabled", "");
    submitButton?.setAttribute("aria-busy", "true");
    let success = false;

    try {
      const payload = {
        data: {
          type: "back-in-stock-subscription",
          attributes: {
            profile: {
              data: {
                type: "profile",
                attributes: {
                  email: emailInput.value.trim(),
                },
              },
            },
            channels: ["EMAIL"],
          },
          relationships: {
            variant: {
              data: {
                type: "catalog-variant",
                id: `$shopify:::$default:::${variantId}`,
              },
            },
          },
        },
      };

      const response = await fetch(
        `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${encodeURIComponent(
          companyId
        )}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            revision: REVISION,
          },
          body: JSON.stringify(payload),
        }
      );
      console.log("BIS submission response", { response });
      if (response.status === 202 || response.ok) {
        success = true;
        fields?.setAttribute("hidden", "");
        successBanner?.removeAttribute("hidden");
      } else {
        errorBanner?.removeAttribute("hidden");
      }
    } catch (error) {
      errorBanner?.removeAttribute("hidden");
    } finally {
      submitButton?.removeAttribute("aria-busy");
      if (success) {
        submitButton?.setAttribute("disabled", "");
      } else {
        updateSubmitState(form);
      }
    }
  };

  document.addEventListener("variant:change", (event) => {
    const form = event.target instanceof HTMLFormElement ? event.target : null;
    if (!form) {
      return;
    }

    // Re-initialize wrappers first in case product:rerender replaced the DOM
    initWrappers();
    handleVariantUpdate(form, event.detail?.variant);
  });

  document.addEventListener("product:rerender", (event) => {
    const form = event.target instanceof HTMLFormElement ? event.target : null;
    if (!form) {
      return;
    }

    setTimeout(() => {
      handleVariantUpdate(form, null);
    }, 0);
  });

  document.addEventListener("change", (event) => {
    const input =
      event.target instanceof HTMLInputElement ? event.target : null;
    if (!input || input.name !== "id") {
      return;
    }

    const form = input.form;
    if (!form) {
      return;
    }

    handleVariantUpdate(form, null);
  });

  document.addEventListener("input", (event) => {
    const input =
      event.target instanceof HTMLInputElement ? event.target : null;
    if (!input || input.name !== "email") {
      return;
    }

    const form = input.closest(FORM_SELECTOR);
    if (!form) {
      return;
    }

    updateSubmitState(form);
  });

  document.addEventListener("submit", (event) => {
    const form = event.target instanceof HTMLFormElement ? event.target : null;
    if (!form || !form.matches(FORM_SELECTOR)) {
      return;
    }

    event.preventDefault();
    submitForm(form);
  });

  document.addEventListener("dialog:after-hide", (event) => {
    const modal = event.target instanceof HTMLElement ? event.target : null;
    if (!modal || !modal.matches(MODAL_SELECTOR)) {
      return;
    }

    const bisId = modal.dataset?.bisId;
    const wrapper = bisId
      ? document.querySelector(
          `${WRAPPER_SELECTOR}[data-bis-id="${CSS.escape(bisId)}"]`
        )
      : null;
    resetWrapperState(wrapper);
  });

  document.addEventListener("dialog:after-show", (event) => {
    const modal = event.target instanceof HTMLElement ? event.target : null;
    if (!modal || !modal.matches(MODAL_SELECTOR)) {
      return;
    }

    const bisId = modal.dataset?.bisId;
    const wrapper = bisId
      ? document.querySelector(
          `${WRAPPER_SELECTOR}[data-bis-id="${CSS.escape(bisId)}"]`
        )
      : null;
    resetWrapperState(wrapper);
  });

  document.addEventListener(
    "click",
    (event) => {
      const trigger =
        event.target instanceof Element
          ? event.target.closest(".klaviyo-bis__trigger")
          : null;
      if (!trigger) {
        return;
      }

      const wrapper = trigger.closest(WRAPPER_SELECTOR);
      resetWrapperState(wrapper);
    },
    true
  );

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initWrappers);
  } else {
    initWrappers();
  }
})();
